import {
	formatDate,
	registerLocaleData
}                        from '@angular/common';
import localeDE          from '@angular/common/locales/de';
import localeEN          from '@angular/common/locales/en-GB';
import { UiService }     from '@app/shared/services/ui.service';
import { APP_CONFIG }    from '@appConfig/_app-config_master';
import { APP_MODE }      from '@appConfig/app-mode-config';
import {
	LanguageDateFormat,
	LanguageEnum,
	LocaleEnum
}                        from '@models/language';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import {
	NumberUtils,
	ObjectUtils
}                        from '@utils/helpers/index';
import DateTimeFormatPart = Intl.DateTimeFormatPart;

registerLocaleData( localeDE, 'de' );
registerLocaleData( localeEN, 'en' );

export const ISO_DATE_FORMAT = 'yyyy-MM-dd';

export const DAY_FORMAT: LanguageDateFormat = {
	[ LanguageEnum.ENGLISH ]: 'MMMM DD, YYYY',
	[ LanguageEnum.GERMAN ]: 'DD. MMMM YYYY'
};

export const MONTH_FORMAT: LanguageDateFormat = {
	[ LanguageEnum.ENGLISH ]: 'MMMM, YYYY',
	[ LanguageEnum.GERMAN ]: 'MMMM YYYY'
};

/**
 * One hour (3600 seconds) in milliseconds,
 * for use in calculations with UNIX timestamps
 */
export const ONE_HOUR_MILLISECONDS = 3_600_000; // 3600 * 1000

/**
 * One day (24 hours) in milliseconds,
 * for use in calculations with UNIX timestamps
 */
export const ONE_DAY_MILLISECONDS = 24 * ONE_HOUR_MILLISECONDS;

export enum DatePrecisionLevelEnum {
	DAY = 'day',
	HOUR = 'hour',
	MONTH = 'month',
	YEAR = 'year',
	MILLISECOND = 'millisecond'
}

/**
 * Constants correspond to the parts of an `Intl.DateTimeFormat` object
 * see, e.g. https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/formatToParts
 * for an explanation
 */
export enum DatePartsEnum {
	DAY = 'day',
	DAY_PERIOD = 'dayPeriod',
	ERA = 'era',
	HOUR = 'hour',
	LITERAL = 'literal',
	MINUTE = 'minute',
	MONTH = 'month',
	SECOND = 'second',
	TIME_ZONE_NAME = 'timeZoneName',
	WEEKDAY = 'weekday',
	YEAR = 'year'
}

export enum DateRelationEnum {
	IS_PAST = 'is past',
	IS_SAME = 'is same',
	IS_FUTURE = 'is future',
}

export interface IFormattedDateParts {
	day?: string;
	dayPeriod?: string;
	era?: string;
	hour?: string;
	literal?: string;
	minute?: string;
	month?: string;
	second?: string;
	timeZoneName?: string;
	weekday?: string;
	year?: string;
}

/**
 * Takes in one `Date` object and returns the time difference between this Date
 * object and "now" on five different precision levels (YEAR, MONTH, DAY, HOUR,
 * and MILLISECOND).
 *
 * The precision level can be set via the 2nd parameter. Default is MILLISECOND.
 *
 * If the input date is nullish, the function will return `null`.
 *
 * When doing the calculation, the current date will always be subtracted from
 * `date`, i.e. the return value will be negative, if `date` is in the past, and
 * positive, if `date` is in the future.
 */
function calculateDifferenceToCurrent( date: Date, precisionLevel?: DatePrecisionLevelEnum ): number {
	return calculateTimeDifference( date, new Date(), precisionLevel );
}

/**
 * Calculates a date relative to the current date.
 *
 * @param offsetInDays the difference in days to the current date: a positive
 * number will yield a future date, a negative number will yield a date in the
 * past. Note that floating-pont numbers will be rounded to the nearest integer,
 * i.e. 1.5 will yield a date 2 days in the future, -1.5 will yield a date 2
 * days in the past.
 */
function calculateRelativeDate( offsetInDays: number = 0 ): Date {
	if ( NumberUtils.isNanOrInfinity( offsetInDays ) ) { offsetInDays = 0 }
	if ( !NumberUtils.numbersAreIntegers( offsetInDays ) ) { offsetInDays = Math.round( offsetInDays ); }
	const date = new Date();
	date.setDate( date.getDate() + offsetInDays );

	return date;
}

/**
 * Takes in two `Date` objects, `date` and `referenceDate`, and returns the time
 * difference between these two Date objects on five different precision levels
 * (`YEAR`, `MONTH`, `DAY`, `HOUR`, and `MILLISECOND`).
 *
 * The precision level can be set via the 3rd parameter. Default is `MILLISECOND`.
 *
 * If one of the input dates is nullish, the function will return `null`.
 *
 * When doing the calculation, `referenceDate` will always be subtracted from
 * `date`, i.e. the return value will be positive, if `referenceDate`
 * is in the past relative to `date`, and negative, if `referenceDate` is in the
 * future relative to `date`.
 *
 * IMPORTANT NOTE: Make sure you always use an appropriate precision level in
 * order to avoid getting misleading results!
 * For example, if `date` is 1st January at 4 AM, and `referenceDate` is
 * 31st December at 10 PM in the previous year, the function will return a time
 * difference of 1 year if precision level is YEAR and 1 month if precision
 * level is `MONTH`, despite the fact that the actual time difference is only
 * 6 hours!
 */
function calculateTimeDifference(
	date: Date,
	referenceDate: Date,
	precisionLevel?: DatePrecisionLevelEnum
): number {
	if ( isInvalidDate( date ) || isInvalidDate( referenceDate ) ) { return null; }

	let result: number;

	switch ( precisionLevel ) {

		case DatePrecisionLevelEnum.DAY:
			date = truncateTimeOfDay( date );
			referenceDate = truncateTimeOfDay( referenceDate );
			const differenceInTime: number = date.getTime() - referenceDate.getTime();
			/*
			 * Rounding makes sure that `result` is --always-- an integer (since this
			 * is not guaranteed by the truncation of the date alone!) and that the
			 * results are consistent.
			 * When working with CET, it can happen that the period between the two
			 * days has one hour added or missing due to the switch from or to CEST,
			 * respectively. This is corrected by the rounding.
			 * The ISO date strings in the DB data do not include timezone info, hence
			 * `Date` will treat the times of day therein always as the actual time of
			 * day on this very day, according to the system's locale settings.
			 *
			 * Example:
			 *
			 * new Date( '2024-09-23T14:31:05.1234' )
			 * --> 'Mon Sep 23 2024 14:31:05 GMT+0200 (Mitteleuropäische Sommerzeit)'
			 * --> 2024-09-23T12:31:05.000Z (UTC/GMT)!
			 *
			 * however
			 *
			 * new Date( '2024-10-28T14:31:05.1234' )
			 * --> 'Mon Oct 28 2024 14:31:05 GMT+0100 (Mitteleuropäische Normalzeit)'!
			 * --> 2024-10-28T13:31:05.123Z (UTC/GMT)!
			 *
			 * So "internally" (i.e. relative to UTC/GMT), the time difference
			 * between these two dates includes one additional hour!
			 */
			result = Math.round( differenceInTime / ONE_DAY_MILLISECONDS );
			break;

		case DatePrecisionLevelEnum.HOUR:
			const truncatedDateTimestamp: number =
				new Date( date ).setHours( date.getHours(), 0, 0, 0 );

			const truncatedReferenceDateTimestamp: number =
				new Date( referenceDate ).setHours( referenceDate.getHours(), 0, 0, 0 );

			const diffedTimestamp: number = truncatedDateTimestamp - truncatedReferenceDateTimestamp;

			result = diffedTimestamp / ONE_HOUR_MILLISECONDS;
			break;

		case DatePrecisionLevelEnum.MONTH:

			const yearsDiff: number = date.getFullYear() - referenceDate.getFullYear();
			const yearsDiffInMonths = yearsDiff * 12;
			const monthDiff: number = date.getMonth() - referenceDate.getMonth();
			result = yearsDiffInMonths + monthDiff;
			break;

		case DatePrecisionLevelEnum.YEAR:
			result = date.getFullYear() - referenceDate.getFullYear();
			break;

		default: // MILLISECONDS
			result = date.getTime() - referenceDate.getTime();
	}
	return result;
}

/**
 * Takes in a JS `Date` object and returns a corresponding `NgbDateStruct` object.
 * Default precision level is DAY.
 */
function dateToNgbDateStruct( date: Date, precision?: DatePrecisionLevelEnum ): NgbDateStruct {
	if ( isInvalidDate( date ) ) { return null; }

	const isFirstMonth = precision === DatePrecisionLevelEnum.YEAR;
	const isFirstDay = isFirstMonth || precision === DatePrecisionLevelEnum.MONTH;

	const ngbDateStruct: NgbDateStruct = {
		year: date.getFullYear(),
		month: isFirstMonth ? 1 : date.getMonth() + 1,
		day: isFirstDay ? 1 : date.getDate()
	};
	return ngbDateStruct;
}

// TODO -- function is part of a set of date formatter functions redundantly reproducing Angular built-in date formatting functionality
function formatAndSplitDateToParts( date: Date, options: Intl.DateTimeFormatOptions, dateParts: DatePartsEnum[], locale?: LanguageEnum ): IFormattedDateParts {
	const formattedDateParts: DateTimeFormatPart[] = formatDateToParts( date, options, locale );
	return getFormattedDateParts( formattedDateParts, dateParts );
}

/*
 * TODO NOTE: Function below is mainly used for UI-related date formatting.
 *  In this regard it seems to have a strong overlap with the function
 *  `getLocaleDateString()`, so it might make sense to use only one of the
 *  two functions for this purpose or merge the two into one.
 */
/**
 * Converts a `Date` object into a date string according to the current UI
 * language (utilizing Angular's built-in `formatDate()` function).
 * @param date {Date} The `Date` object to be converted to a date string
 * @param format {string} A string defining the composition and format of the
 * date string. For details and examples see
 * https://v17.angular.io/api/common/DatePipe#custom-format-options
 * @param uiService {UiService} Reference of the `UiService` providing
 * information on the current UI language
 * @return A date string formatted according to the provided locale and format
 * string.
 */
function formatDateToCurrentLocale( date: Date, format: string, uiService: UiService ): string {
	if ( isInvalidDate( date ) || !uiService ) { return null; }
	const locale = uiService.getLanguage( true );
	return formatDate( date, format, locale );
}

// NOTE: Function below is mainly used for REST API-related (EMO/TMO -> DTO) date formatting
/**
 * Converts a `Date` object into a date string (utilizing Angular's built-in
 * `formatDate()` function).
 * @param date {Date|string} The `Date` object to be converted to a formatted
 * date string, or a valid date string to be reformatted.
 * @param format {string} A string defining the composition and format of the
 * date string. For details and examples see
 * https://v17.angular.io/api/common/DatePipe#custom-format-options.
 * @param locale {string} A locale code (e.g. `'de-de'`) determining language-
 * and country-specific parts of the formatted string, especially names of
 * weekdays and months; default is `'en'`
 * @return A date string formatted according to the provided locale and format
 * string, `null` if no valid `Date` is provided.
 */
function formatDateToLocale( date: Date | string, format: string, locale: string ): string {
	/*
	 * Function has to support strings, because it is used to create the ISO date
	 * string on list XHRs, which already can be a (long ISO date) string when the
	 * XHR is based on ListOps data from the NgRx store (NgRx store does not
	 * support non-serializable data types such as Date, Map, Set, HTMLElement etc.)!
	 */
	if ( typeof date === 'string' ) { date = new Date( date ); }
	/*
	 * `formatDate()` will throw an error when the input Date is an Invalid Date,
	 * or the input date string cannot be parsed into a valid date!
	 */
	if ( isInvalidDate( date ) ) { return null; }
	// `formatDate()` will throw an error when locale is falsy (nullish or empty string)!
	if ( !locale ) { locale = 'en'; }
	return formatDate( date, format, locale );
}

// TODO -- function is part of a set of date formatter functions redundantly reproducing Angular built-in date formatting functionality
function formatDateToParts( date: Date, options: Intl.DateTimeFormatOptions, locale?: LanguageEnum ): DateTimeFormatPart[] {
	return new Intl.DateTimeFormat( locale ?? LanguageEnum.ENGLISH, options ).formatToParts( date );
}

/**
 * Compares a date to a reference date either exactly (default), by day,
 * month, or year, and returns whether the date is in the past, at the same time
 * or in the future relative to the reference date.
 */
function getDateRelation(
	date: Date,
	referenceDate: Date,
	precision: DatePrecisionLevelEnum = DatePrecisionLevelEnum.MILLISECOND
): DateRelationEnum {
	if ( isInvalidDate( date ) || isInvalidDate( referenceDate ) ) { return null; }

	const timeDiff = calculateTimeDifference( date, referenceDate, precision );

	const resultMap: { [ key in DateRelationEnum ]: boolean } = {
		[ DateRelationEnum.IS_PAST ]: timeDiff < 0,
		[ DateRelationEnum.IS_SAME ]: timeDiff === 0,
		[ DateRelationEnum.IS_FUTURE ]: timeDiff > 0,
	};

	return Object.keys( resultMap ).find(
		( key: DateRelationEnum ) => resultMap[ key ]
	) as DateRelationEnum;
}

function getFirstDateOfMonth( date?: Date ): Date {
	if ( isInvalidDate( date ) ) { date = new Date(); }
	return new Date( date?.getFullYear(), date?.getMonth(), 1 );
}

// TODO -- function is part of a set of date formatter functions redundantly reproducing Angular built-in date formatting functionality
function getFormattedDateParts( datePartsList: DateTimeFormatPart[], dateParts: DatePartsEnum[] ): IFormattedDateParts {
	const filteredParts: IFormattedDateParts = {};
	datePartsList?.forEach( ( dateFormatPart: DateTimeFormatPart ) => {
		if ( (
			dateParts as string[]
		)?.includes( dateFormatPart.type ) ) {
			filteredParts[ dateFormatPart.type ] = dateFormatPart.value;
		}
	} );
	return filteredParts;
}

function getLastDateOfMonth( date?: Date ): Date {
	if ( isInvalidDate( date ) ) { date = new Date(); }
	return new Date( date?.getFullYear(), date?.getMonth() + 1, 0 );
}

function getLastDateOfYear( date?: Date ): Date {
	if ( isInvalidDate( date ) ) { date = new Date(); }
	return new Date( date.getFullYear() + 1, 0, 0 );
}

/*
 * TODO NOTE: Function below is mainly used for UI-related date formatting.
 *  In this regard it seems to have a strong overlap with the function
 *  `formatDateToCurrentLocale()`, so it might make sense to use only one of the
 *  two functions for this purpose or merge the two into one.
 */
/**
 * Converts a `Date` object into a date string (utilizing the genuine JS method
 * `Date.prototype.toLocaleDateString()`).
 * @param date The `Date` object to be converted to a date string
 * @param locale The identifier of a supported locale, default is `'de-de'`
 * @param options Options to define the actual
 * format of the date string. If no format options are provided, the date string
 * will have the default format (`{ day: '2-digit', month: 'short', year: 'numeric' }`).
 * @return A date string formatted according to the provided locale and format
 * options.
 */
function getLocaleDateString(
	date: Date,
	locale: LocaleEnum,
	options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' }
): string {
	if ( isInvalidDate( date ) ) { return null; }
	/*
	 * default value for `locale` is needed for unit tests, because,...
	 * ...if undefined, the default locale is used, which can vary across the
	 * systems and may cause tests to fail in deployment pipeline.
	 * ...if null, an error is thrown.
	 */
	return date.toLocaleDateString( locale ?? LocaleEnum.GERMAN , options );
}

// TODO -- function is part of a set of date formatter functions redundantly reproducing Angular built-in date formatting functionality
/**
 * @param date the date to be formatted, function will use the current date if
 *             this value is nullish or an Invalid Date
 * @param formatOptions the formatting rules for the individual date parts
 *             according to `Intl.DateTimeFormatOptions`
 * @param datePartsOrder defines the order of the date parts by the order of the
 *             enum constants in the array (has to match the used
 *             `formatOptions`, e.g. it doesn't make sense to have `YEAR` in
 *             `datePartsOrder` but only have { day: 'numeric' } in the options)
 * @param separator optional separator for the date parts, default is without separation
 *
 * @returns date string, formatted and ordered as defined by parameters
 */
function getStringFormattedDate(
	date: Date,
	formatOptions: Intl.DateTimeFormatOptions,
	datePartsOrder: DatePartsEnum[],
	separator?: string
): string {
	// without formatOptions or datePartsOrder the function will either produce TypeErrors or won't return anything, respectively
	if ( !formatOptions || !datePartsOrder ) { return null; }

	const dateParts: DatePartsEnum[] = [];

	Object.keys( formatOptions )
	.forEach( ( formatOptionKey: string ) => {
		switch ( formatOptionKey ) {
			case 'day':
				dateParts.push( DatePartsEnum.DAY );
				break;

			case 'hour':
				dateParts.push( DatePartsEnum.HOUR );
				break;

			case 'minute':
				dateParts.push( DatePartsEnum.MINUTE );
				break;

			case 'month':
				dateParts.push( DatePartsEnum.MONTH );
				break;

			case 'second':
				dateParts.push( DatePartsEnum.SECOND );
				break;

			case 'weekday':
				dateParts.push( DatePartsEnum.WEEKDAY );
				break;

			case 'year':
				dateParts.push( DatePartsEnum.YEAR );
				break;

		}
	} );

	const currentDateParts: IFormattedDateParts = formatAndSplitDateToParts(
		isInvalidDate( date ) ? new Date() : date,
		formatOptions,
		dateParts
	);

	separator = ObjectUtils.isNullish( separator ) ? '' : separator;
	return datePartsOrder?.map( ( datePart: DatePartsEnum ) => {
		if ( currentDateParts[ datePart ] ) {
			return currentDateParts[ datePart ];
		}
	} ).join( separator );
}

/**
 * Checks a JS Date object for validity
 * @returns `true` if the input `Date` object is nullish or holds an
 * `Invalid Date` or (on runtime) might not be a `Date` object in the first
 * place, `false` for any other `Date` object
 */
function isInvalidDate( date: Date ): boolean {
	/*
	 * checking for `Invalid Date` by `Number.isNaN()`
	 * works because a valid Date is converted into a UNIX timestamp when cast
	 * to `number`, while a non-nullish invalid date is converted to NaN
	 */
	return !date || !( date instanceof Date ) || Number.isNaN( +date );
}

function isSameMonth( date1: Date, date2: Date ): boolean {
	if ( isInvalidDate( date1 ) || isInvalidDate( date2 ) ) { return false; }
	const isMonthSame = date1.getMonth() === date2.getMonth();
	const isYearSame = date1.getFullYear() === date2.getFullYear();
	return isMonthSame && isYearSame;
}

function isToday( date: Date ): boolean {
	const today = new Date();
	return isSameMonth( date, today ) && date?.getDate() === today?.getDate();
}

/**
 * Takes in a `NgbDateStruct` object and returns a corresponding JS `Date` object.
 * Default precision level is DAY.
 */
function ngbDateStructToDate( ngbDateStruct: NgbDateStruct, precision?: DatePrecisionLevelEnum ): Date {
	const isFirstMonth = precision === DatePrecisionLevelEnum.YEAR;
	const isFirstDay = isFirstMonth || precision === DatePrecisionLevelEnum.MONTH;

	const date = new Date(
		ngbDateStruct?.year,
		isFirstMonth ? 0 : ngbDateStruct?.month - 1,
		isFirstDay ? 1 : ngbDateStruct?.day
	);
	return isInvalidDate( date ) ? null : date;
}

/**
 * Takes in a date, creates a copy from it, changes (shifts) the copy by
 * the given values and returns it, leaving the original date unchanged.
 * @param date The date to be shifted
 * @param shiftBy An object containing the days, months and years by which the
 *                date is shifted. Positive values will shift the date towards
 *                the future, negative ones will shift it towards the past. Note
 *                that floating-pont numbers will be rounded to the nearest
 *                integer.
 * @returns A new `Date` object with the date shifted as defined in the `shiftBy`
 *          options, relative to the values of the input `Date`. If `shiftBy` is
 *          nullish or completely empty, the returned `Date` will be an
 *          identical copy of the input `Date`. If input `Date` is nullish or
 *          invalid, the function will return `null`.
 */
function shiftDate( date: Date, shiftBy: { days?: number, months?: number, years?: number } ): Date {
	if ( isInvalidDate( date ) ) { return null; }
	shiftBy = shiftBy ?? {};
	Object.keys( shiftBy ).forEach( ( key: string ) => {
		if ( NumberUtils.isNanOrInfinity( shiftBy[ key ] ) ) { shiftBy[ key ] = 0; }
		if ( !NumberUtils.numbersAreIntegers( shiftBy[ key ] ) ) { shiftBy[ key ] = Math.round( shiftBy[ key ] ); }
	} );

	const dateCopy = new Date( date );

	if ( shiftBy.days ) { dateCopy.setDate( date.getDate() + shiftBy.days ); }
	if ( shiftBy.months ) { dateCopy.setMonth( date.getMonth() + shiftBy.months ); }
	if ( shiftBy.years ) { dateCopy.setFullYear( date.getFullYear() + shiftBy.years ); }

	return dateCopy;
}

/**
 * Creates a copy of the `Date` object passed to the function, sets all time
 * units below day-level in that new `Date` object to 0, and eventually returns
 * the new `Date` object.
 *
 * Returns the truncated current date ("today") if the `Date` object passed to
 * the function object is nullish or an `Invalid Date`.
 */
function truncateTimeOfDay( date: Date ): Date {
	if ( isInvalidDate( date ) ) { date = new Date(); }

	const dateCopy = new Date( date );
	dateCopy.setHours( 0, 0, 0, 0 );

	return dateCopy;
}

/**
 * Converts a `yyyyMM`-formatted date string to a `Date` object, setting the day
 * to 1 (i.e. to first day of month)
 */
function yearMonthStringToDate( dateString: string ): Date {
	const dateStringFormatPattern = new RegExp(/^(19|20)\d{2}(0[1-9]|1[0-2])$/); // also checks for plausibility, so 202413 won't go through
	if ( !dateString || typeof dateString !== 'string' || !dateStringFormatPattern.test( dateString ) ) {
		if ( APP_CONFIG.appMode === APP_MODE.LOCAL ) { // log only in local dev environment
			console.warn( 'DateUtils:yearMonthStringToDate -> nullish or inappropriate/implausible value provided' );
		}
		return null;
	}
	const year = dateString?.substring( 0, 4 );
	const month = dateString?.substring( 4, 6 );

	return new Date( `${ year }-${ month }-01` );
}


export {
	calculateDifferenceToCurrent,
	calculateRelativeDate,
	calculateTimeDifference,
	dateToNgbDateStruct,
	formatAndSplitDateToParts,
	formatDateToCurrentLocale,
	formatDateToLocale,
	formatDateToParts,
	getDateRelation,
	getFirstDateOfMonth,
	getFormattedDateParts,
	getLastDateOfMonth,
	getLastDateOfYear,
	getLocaleDateString,
	getStringFormattedDate,
	isInvalidDate,
	isSameMonth,
	isToday,
	ngbDateStructToDate,
	shiftDate,
	truncateTimeOfDay,
	yearMonthStringToDate
};
